<template>
  <v-dialog v-model="localDialog" persistent scrollable max-width="680px">
    <v-card class="profile-dialog-card material-assignment-dialog">
      <div class="profile-dialog-header material-assignment-dialog__header">
        <div class="material-assignment-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">mdi-file-document-edit-outline</v-icon>
          </v-avatar>
          <div>
            <span>{{ sourceLabel }}</span>
            <h2>Assign materials covered</h2>
            <p>Connect a shared material template to this classroom and term.</p>
          </div>
        </div>
        <v-btn icon aria-label="Close material assignment dialog" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />
      <v-card-text v-if="selectedClassRoom" class="profile-dialog-body material-assignment-dialog__body">
        <section class="material-assignment-dialog__classroom">
          <div class="material-assignment-dialog__classroom-icon">
            <v-icon color="primary">mdi-door-open</v-icon>
          </div>
          <div>
            <strong>{{ selectedClassRoom.name || "Unnamed classroom" }}</strong>
            <span>{{ selectedClassRoom.Class ? selectedClassRoom.Class.name : "No department" }}</span>
          </div>
        </section>

        <section class="material-assignment-dialog__section">
          <div class="material-assignment-dialog__section-heading">
            <strong>Reporting period</strong>
            <span>Select the year and term for this material assignment.</span>
          </div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
          v-model="selectedYear"
          :items="years"
          item-text="name"
          return-object
          outlined
          dense
          label="Select Year"
          @change="loadTerms"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
          v-model="selectedTerm"
          :items="terms"
          item-text="name"
          return-object
          outlined
          dense
          label="Select Term"
          :disabled="!selectedYear"
              />
            </v-col>
          </v-row>
        </section>

        <section class="material-assignment-dialog__section">
          <div class="material-assignment-dialog__section-heading">
            <strong>Material template</strong>
            <span>The same shared templates are available to regular and virtual classrooms.</span>
          </div>
          <v-select
          v-model="selectedTemplate"
          :items="templates"
          item-text="name"
          return-object
          outlined
          dense
          label="Select Template"
          />
          <div v-if="selectedTemplate" class="material-assignment-dialog__template-summary">
            <v-icon color="primary" size="18">mdi-format-list-checks</v-icon>
            <span>{{ selectedTemplate.fields ? selectedTemplate.fields.length : 0 }} template fields</span>
          </div>
        </section>
      </v-card-text>

      <v-divider />
      <v-card-actions class="profile-dialog-actions material-assignment-dialog__actions">
        <span>{{ canSave ? "Ready to assign" : "Complete all selections" }}</span>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="saving" @click="save" :disabled="!canSave">
          Assign materials
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import termMaterialsService from "@/modules/TermMaterials/service";
import yearService from "@/modules/ReportCard/Year/service";
import termService from "@/modules/ReportCard/Terms/service";

export default {
  name: "AssignTermMaterialTemplate",
  props: {
    dialog: { default: false },
    selectedClassRoom: { type: Object },
    source: { type: String, default: "regular" },
  },
  data() {
    return {
      localDialog: false,
      saving: false,
      years: [],
      terms: [],
      templates: [],
      selectedYear: null,
      selectedTerm: null,
      selectedTemplate: null,
    };
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
      if (val) this.init();
    },
  },
  computed: {
    sourceLabel() {
      return this.source === "virtual" ? "Virtual classroom" : "Classroom";
    },
    canSave() {
      return !!(this.selectedClassRoom && this.selectedTerm && this.selectedTemplate);
    },
  },
  methods: {
    async init() {
      this.selectedYear = null;
      this.selectedTerm = null;
      this.selectedTemplate = null;
      this.terms = [];

      const yr = await yearService.getAll({
        filteredClasses: [this.selectedClassRoom.ClassId],
      });
      this.years = (yr && yr.data) ? yr.data : [];

      const t = await termMaterialsService.getTemplates();
      this.templates = (t && t.data && t.data) ? t.data : [];
    },

    async loadTerms() {
      this.selectedTerm = null;
      this.terms = [];
      if (!this.selectedYear) return;

      const res = await termService.getAll({ YearId: this.selectedYear.id });
      this.terms = (res && res.data) ? res.data : [];
    },

    close() {
      this.$emit("close");
    },

    async save() {
      if (!this.canSave) return;

      this.saving = true;
      try {
        await termMaterialsService.assignTemplate({
          [this.source === "virtual" ? "VirtualClassRoomId" : "ClassRoomId"]: this.selectedClassRoom.id,
          TermId: this.selectedTerm.id,
          TermMaterialTemplateId: this.selectedTemplate.id,
        });

        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "success", title: "Assigned" });

        this.$emit("saved");
        this.close();
      } catch (e) {
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "error", title: (e && e.data && e.data.message) ? e.data.message : "Assign failed" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.material-assignment-dialog { overflow: hidden; border-radius: 14px !important; }
.material-assignment-dialog__header { background: #fbfcfb; }
.material-assignment-dialog__title { display: flex; align-items: center; gap: 12px; }
.material-assignment-dialog__title > div:last-child { display: flex; flex-direction: column; }
.material-assignment-dialog__title span { color: #43a047; font-size: 10px; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
.material-assignment-dialog__title h2 { margin: 2px 0 0; color: #172033; font-size: 21px; line-height: 1.2; }
.material-assignment-dialog__title p { margin: 3px 0 0; color: #7b8799; font-size: 11px; }
.material-assignment-dialog__body { min-height: 410px; padding: 22px 24px !important; }
.material-assignment-dialog__classroom { display: flex; align-items: center; gap: 11px; padding: 13px; border: 1px solid #e3e9e3; border-radius: 11px; background: #f8faf8; }
.material-assignment-dialog__classroom-icon { display: flex; width: 38px; height: 38px; align-items: center; justify-content: center; border-radius: 9px; background: #eaf6eb; }
.material-assignment-dialog__classroom > div:nth-child(2) { display: flex; flex-direction: column; }
.material-assignment-dialog__classroom strong { color: #253047; font-size: 13px; }
.material-assignment-dialog__classroom span { margin-top: 2px; color: #879288; font-size: 10px; }
.material-assignment-dialog__section { margin-top: 22px; }
.material-assignment-dialog__section-heading { display: flex; flex-direction: column; margin-bottom: 10px; }
.material-assignment-dialog__section-heading strong { color: #344238; font-size: 12px; }
.material-assignment-dialog__section-heading span { margin-top: 2px; color: #8b968e; font-size: 10px; }
.material-assignment-dialog__template-summary { display: flex; align-items: center; gap: 7px; margin-top: -4px; padding: 10px 12px; border-radius: 9px; background: #f1f8f1; color: #4f6253; font-size: 11px; }
.material-assignment-dialog__actions { color: #7b8799; font-size: 10px; }
.material-assignment-dialog__actions .v-btn { border-radius: 8px; text-transform: none; letter-spacing: 0; }
@media (max-width: 599px) { .material-assignment-dialog__body { min-height: 360px; padding: 16px !important; } .material-assignment-dialog__title p { display: none; } }
</style>
